import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { HardHatIcon, PackageIcon, TruckIcon, FactoryIcon, StoreIcon, HeartIcon, ArrowRightIcon } from 'lucide-react';
import { applications } from '../data/applications';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../lib/i18n';
interface ApplicationsPageProps {
  onRequestQuote: () => void;
}
export function ApplicationsPage({
  onRequestQuote
}: ApplicationsPageProps) {
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const ru = lang === 'ru';
  const iconMap: Record<string, ComponentType<{
    className?: string;
  }>> = {
    HardHat: HardHatIcon,
    Package: PackageIcon,
    Truck: TruckIcon,
    Factory: FactoryIcon,
    Store: StoreIcon,
    Heart: HeartIcon
  };
  return <main className="min-h-screen bg-dark-bg" data-id="element-670">
      {/* Hero */}
      <section className="py-20 bg-dark-surface border-b-2 border-dark-border" data-id="element-671">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-672">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6" data-id="element-673">
            <span className="text-brand-yellow" data-id="element-674">
              {en ? 'Applications' : ru ? 'Применение' : 'Primene'}
            </span>{' '}
            {en ? 'across industries' : ru ? 'в различных отраслях' : 'u različitim industrijama'}
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-675">
            {en ? 'D-TECH equipment solves real challenges across professional environments. Discover how our solutions can improve your operations.' : ru ? 'Оборудование D-TECH решает реальные задачи в профессиональной среде. Узнайте, как наши решения могут улучшить ваши операции.' : 'D-TECH oprema rešava realne izazove u različitim profesionalnim okruženjima. Otkrijte kako naša rešenja mogu unaprediti vaše operacije.'}
          </p>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-20" data-id="element-676">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-677">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-678">
            {applications.map(app => {
            const Icon = iconMap[app.icon] || PackageIcon;
            const title = en && app.titleEn ? app.titleEn : ru && app.titleRu ? app.titleRu : app.title;
            const problem = en && app.problemEn ? app.problemEn : ru && app.problemRu ? app.problemRu : app.problem;
            const solution = en && app.solutionEn ? app.solutionEn : ru && app.solutionRu ? app.solutionRu : app.solution;
            return <Card key={app.id} hover data-id="element-679">
                  <div className="flex items-start space-x-4 mb-6" data-id="element-680">
                    <div className="w-14 h-14 bg-brand-yellow rounded-industrial flex items-center justify-center flex-shrink-0" data-id="element-681">
                      <Icon className="w-7 h-7 text-dark-bg" data-id="element-682" />
                    </div>
                    <div className="flex-1" data-id="element-683">
                      <h3 className="text-2xl font-bold text-white mb-2" data-id="element-684">
                        {title}
                      </h3>
                    </div>
                  </div>
                  <div className="space-y-4" data-id="element-685">
                    <div data-id="element-686">
                      <h4 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-2" data-id="element-687">
                        {en ? 'Problem' : ru ? 'Проблема' : 'Problem'}
                      </h4>
                      <p className="text-gray-300 leading-relaxed" data-id="element-688">{problem}</p>
                    </div>
                    <div data-id="element-689">
                      <h4 className="text-sm font-bold text-brand-yellow uppercase tracking-wider mb-2" data-id="element-690">
                        {en ? 'Solution' : ru ? 'Решение' : 'Rešenje'}
                      </h4>
                      <p className="text-white leading-relaxed" data-id="element-691">{solution}</p>
                    </div>
                  </div>
                </Card>;
          })}
          </div>

          {/* CTA Section */}
          <div className="bg-dark-surface border-2 border-dark-border rounded-industrial p-12 text-center" data-id="element-692">
            <h2 className="text-3xl font-black text-white mb-4" data-id="element-693">
              {en ? "Don't see your industry?" : ru ? 'Не видите свою отрасль?' : 'Ne vidite vašu industriju?'}
            </h2>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto" data-id="element-694">
              {en ? 'Our equipment adapts to a wide range of professional environments. Contact us to discuss your specific use case.' : ru ? 'Наше оборудование адаптируется к широкому спектру профессиональных сред. Свяжитесь с нами, чтобы обсудить ваш конкретный случай использования.' : 'Naša oprema se prilagođava različitim profesionalnim okruženjima. Kontaktirajte nas da razgovaramo o vašoj specifičnoj primeni.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-695">
              <Button variant="primary" size="lg" onClick={onRequestQuote} data-id="element-696">
                {en ? 'Request a quote' : ru ? 'Запросить цену' : 'Zatražite ponudu'}
              </Button>
              <Link to="/contact" data-id="element-697">
                <Button variant="secondary" size="lg" data-id="element-698">
                  {en ? 'Contact us' : ru ? 'Свяжитесь с нами' : 'Kontaktirajte nas'}
                  <ArrowRightIcon className="w-5 h-5 ml-2 inline" data-id="element-699" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>;
}